import { SubscriptionService } from '@affine/core/modules/cloud';
import { Trans, useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import IconFont from '@ys/repo-ui/base/IconFont';
import { UserCreditsDetailsService } from '@affine/core/modules/cloud/services/user-credits-detail';
import * as style from './index.css';
import { CreditsMap } from '@affine/core/modules/cloud/server/type/credits';
import { SubscriptionPlan } from '@affine/graphql';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useHoverSupport } from '@affine/core/components/hooks/use-hover-support';
import CreditsTooltip from '../credits-tooltip';

interface CreditsDetailProps {
  position: 'simpleSidrbar' | 'settingDialog' | 'rootSideBar';
}

const CreditsDetail = ({ position }: CreditsDetailProps) => {
  const creditsDetail = useService(UserCreditsDetailsService).creditsDetail;
  const workspaceDialogService = useService(WorkspaceDialogService);
  const subscriptionService = useService(SubscriptionService);
  const dailyCredits = useLiveData(creditsDetail.dailyCredits$);
  const credits = useLiveData(creditsDetail.credits$);
  const creditsList = useLiveData(creditsDetail.creditsList$);
  const proPlan = useLiveData(
    subscriptionService.subscription.pro$.map(subscription =>
      subscription !== null ? subscription : null
    )
  );
  const studioPlan = useLiveData(
    subscriptionService.subscription.studio$.map(subscription =>
      subscription !== null ? subscription : null
    )
  );
  const expertPlan = useLiveData(
    subscriptionService.subscription.expert$.map(subscription =>
      subscription !== null ? subscription : null
    )
  );
  const t = useI18n();
  const currentPlan = useLiveData(
    subscriptionService.subscription.currentPlan$
  );
  const expiredDate = useMemo(() => {
    if (currentPlan === SubscriptionPlan.Studio && studioPlan !== null) {
      return studioPlan?.end?.split('T')[0];
    } else if (currentPlan === SubscriptionPlan.Expert && expertPlan !== null) {
      return expertPlan?.end?.split('T')[0];
    } else if (currentPlan === SubscriptionPlan.Pro && proPlan !== null) {
      return proPlan?.end?.split('T')[0];
    } else {
      return '';
    }
  }, [currentPlan, studioPlan, proPlan, expertPlan]);

  const dateI18nKey = useMemo(() => {
    const plan =
      currentPlan === SubscriptionPlan.Studio
        ? studioPlan
        : currentPlan === SubscriptionPlan.Expert
          ? expertPlan
          : proPlan;
    return plan?.canceledAt ? 'expired' : 'renew';
    return '';
  }, [currentPlan, studioPlan, proPlan]);

  const [isShowInfo, setIsShowInfo] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isInfoHovering, setIsInfoHovering] = useState(false);
  const supportsHover = useHoverSupport();

  const infoRef = useRef<HTMLDivElement>(null);
  const titleInfo = useMemo(
    () => (
      <div
        className={`${style.creditsContentItemTitleInfo} ${position === 'settingDialog' ? style.creditsContentItemTitleInfoSettingDialog : style.creditsContentItemTitleInfoBottom}`}
        onMouseEnter={() => setIsInfoHovering(true)}
        onMouseLeave={() => setIsInfoHovering(false)}
        ref={infoRef}
      >
        <div className={style.creditsContentItemTitleInfoTitle}>
          <div className={style.creditsContentItemTitleInfoTitleLeft}>
            {t['com.muset.credits.detail.card.item.credit.info.title']()}
          </div>
          <div
            className={style.creditsContentItemTitleInfoTitleRight}
            onClick={() => {
              window.open(`${window.location.origin}/credits-help`, '_blank');
            }}
          >
            {t['com.muset.credits.detail.card.item.credit.info.title.more']()}
          </div>
        </div>
        <div className={style.creditsContentItemTitleInfoContent}>
          <div className={style.creditsContentItemTitleInfoContentleft}>·</div>
          <div className={style.creditsContentItemTitleInfoContentRight}>
            {t[
              'com.muset.credits.detail.card.item.credit.info.description-1'
            ]()}
          </div>
        </div>
        <div className={style.creditsContentItemTitleInfoContent}>
          <div className={style.creditsContentItemTitleInfoContentleft}>·</div>
          <div className={style.creditsContentItemTitleInfoContentRight}>
            {t[
              'com.muset.credits.detail.card.item.credit.info.description-2'
            ]()}
          </div>
        </div>
        <div className={style.creditsContentItemTitleInfoContent}>
          <div className={style.creditsContentItemTitleInfoContentleft}>·</div>
          <div className={style.creditsContentItemTitleInfoContentRight}>
            {t[
              'com.muset.credits.detail.card.item.credit.info.description-3'
            ]()}
          </div>
        </div>
        <div className={style.creditsContentItemTitleInfoContent}>
          <div className={style.creditsContentItemTitleInfoContentleft}>·</div>
          <div className={style.creditsContentItemTitleInfoContentRight}>
            {t[
              'com.muset.credits.detail.card.item.credit.info.description-4'
            ]()}
          </div>
        </div>
      </div>
    ),
    [t, position, supportsHover]
  );

  const onClickCheckMore = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'credits',
    });
  }, [workspaceDialogService, position]);
  const onClickManage = useCallback(() => {
    if (position === 'settingDialog') {
      workspaceDialogService.setActiveTab('plans');
    } else {
      workspaceDialogService.open('setting', {
        activeTab: 'plans',
      });
    }
  }, [workspaceDialogService, position]);

  useEffect(() => {
    if (!isHovering && !isInfoHovering) {
      const timer = setTimeout(() => {
        setIsShowInfo(false);
      }, 100); // 添加一点延迟
      return () => clearTimeout(timer);
    }
  }, [isHovering, isInfoHovering]);

  return (
    <div
      className={`${style.credits} ${position !== 'settingDialog' ? style.needBoxShadow : ''}`}
    >
      <div
        className={`${style.creditsTitle} ${position === 'settingDialog' ? style.settingDialog : ''}`}
      >
        <div className={style.creditsTitleLeft}>
          {currentPlan}
          {position === 'settingDialog' && currentPlan !== 'Free' && (
            <div className={style.creditsDate}>
              <div className={style.creditsDateText}>
                <Trans
                  i18nKey={`com.muset.credits.detail.card.${dateI18nKey}`}
                  values={{ date: expiredDate }}
                  components={{
                    1: <span className={style.creditsDateTextValue} />,
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className={style.creditsTitleRight} onClick={onClickManage}>
          {currentPlan === 'Free'
            ? t['com.muset.credits.detail.card.upgarde']()
            : t['com.muset.credits.detail.card.manage']()}
        </div>
      </div>
      <div className={style.creditsContent}>
        <div className={style.creditsContentItem}>
          <div className={style.creditsContentItemTitle}>
            <IconFont
              className={style.creditsContentItemTitleIcon}
              name="four-star"
              size={16}
            />
            <div className={style.creditsContentItemTitleText}>
              <span className={style.creditsContentItemTitleTextLeft}>
                {t['com.muset.credits.detail.card.item.credit.title']()}
              </span>
              {/* <IconFont
                className={style.creditsContentItemTitleTextRight}
                name="question-circle"
                size={16}
                onMouseEnter={() => {
                  setIsHovering(true);
                  setIsShowInfo(true);
                }}
                onMouseLeave={() => setIsHovering(false)}
              />
              {isShowInfo && titleInfo} */}
              <CreditsTooltip position={position} />
            </div>
            <div className={style.space}></div>
            <div className={style.creditsContentItemTitleValue}>
              {credits.effective - dailyCredits.effective}
            </div>
          </div>

          {creditsList.map(item => {
            return (item.type === CreditsMap.MONTHLY && item.total !== 0) ||
              item.effective !== 0 ? (
              <div key={item.type} className={style.creditsContentItemDetail}>
                <div className={style.creditsContentItemDetailText}>
                  {t[
                    `com.muset.credits.detail.card.item.credit.item.${item.type}`
                  ]()}
                </div>
                <div className={style.space}></div>
                <div className={style.creditsContentItemDetailText}>
                  {item.type !== CreditsMap.MONTHLY
                    ? item.effective
                    : `${item.effective}/${item.total}`}
                </div>
              </div>
            ) : null;
          })}
        </div>
        <div className={style.creditsContentItem}>
          <div className={style.creditsContentItemTitle}>
            <IconFont
              className={style.creditsContentItemTitleIcon}
              name="credits-daily"
              size={16}
            />
            <div className={style.creditsContentItemTitleText}>
              <span className={style.creditsContentItemTitleTextLeft}>
                {t['com.muset.credits.detail.card.item.credit.title.daily']()}
              </span>
              <IconFont
                className={style.creditsContentItemTitleTextRight}
                name="question-icon"
                size={16}
              />
            </div>
            <div className={style.space}></div>
            <div className={style.creditsContentItemTitleValue}>
              {dailyCredits.effective}
            </div>
          </div>
          <div className={style.creditsContentItemDetail}>
            <div className={style.creditsContentItemDetailText}>
              {t[
                `com.muset.credits.detail.card.item.credit.title.daily.description`
              ]({ amount: dailyCredits.total.toString() })}
            </div>
          </div>
        </div>
      </div>
      {position !== 'settingDialog' && (
        <div className={style.creditsBottom} onClick={onClickCheckMore}>
          <div className={style.creditsBottomText}>
            {t['com.muset.credits.detail.card.item.credit.usage.button']()}
          </div>
          <IconFont
            className={style.creditsBottomIcon}
            name="leftArrow"
            size={16}
          />
        </div>
      )}
    </div>
  );
};
export default CreditsDetail;
